import React, { useState } from 'react';
import ReactModal from 'react-modal';
import { RegularButton, AlertAdapter, CommonDropdown, FullScreenLoading, CommonInput } from 'pg-ui-library';
import { AxiosResponse } from 'axios';
import { CloseIcon } from 'src/modules/login/components/mfa-modal/styles';
import closeSvg from 'src/assets/close.svg';
import { useFormik } from 'formik';
import * as yup from 'yup';
import UserService from 'src/services/user.service';
import { useQuery } from '@tanstack/react-query';
import CompanyService from 'src/services/company.services';
import {
  UserAdditionBody,
  CommonInputDiv,
  UserAdditionForm,
  UserAdditionHeader,
  UserAdditionFormRow,
  UserAdditionFormRowLast,
  InputContainer,
} from './styles';
import { updateSupportModule } from '../../mutations/user-setting.mutation';

interface AddUsersProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

interface Module {
  id: string;
  name: string;
  description: string;
  email: string | null;
}

// Define the types for the response and company
interface Company {
  id: string;
  legalEntityId: string;
  name: string;
  phone: string;
  identificationNumber: string;
  email: string;
}

interface CompaniesResponse {
  items: Company[];
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalPages: number;
  };
}

const UpdateSubModuleEmail: React.FC<AddUsersProps> = ({ isOpen, onRequestClose }) => {
  const [selectedModuleId, setSelectedModuleId] = useState<string>('');
  const [selectedModuleName, setSelectedModuleName] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const addEmailSubModule = updateSupportModule();

  const { data: companies } = useQuery<AxiosResponse<any>>({
    queryKey: ['users/companies'],
    queryFn: () => CompanyService.getCompanies(),
    enabled: true,
  });

  const typedCompanies = companies?.data as CompaniesResponse;

  const validationSchema = yup.object().shape({
    email: yup.string().required('Email is required'),
    companyId: yup.string().required('Company is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      name: selectedModuleName,
      companyId: selectedCompany,
    },
    validationSchema,
    onSubmit: async values => {
      try {
        const selectedCompanyData = typedCompanies?.items?.find(company => company.id === values.companyId);

        const payload = {
          id: selectedModuleId,
          name: selectedModuleName,
          email: values.email,
          companyId: values.companyId,
          companyName: selectedCompanyData?.legalEntityId,
        };
        await addEmailSubModule.mutateAsync({ payload });
        AlertAdapter('Email has been added to the Module', 'success');
        onRequestClose();
      } catch (error: any) {
        const errorMsg = error?.toString() || 'An error occurred while adding the support';
        AlertAdapter(errorMsg, 'error');
      }
    },
  });

  const modules = useQuery<Module[]>(['/admin/users/modules'], async () => {
    const result = await UserService.getModules();
    return result.data;
  });

  const renderModuleDropdown = () => {
    if (modules.isLoading) {
      return <FullScreenLoading />;
    }
    if (modules.error) {
      return <p>Error fetching users</p>;
    }

    const options =
      modules.data?.map((module: Module) => ({
        key: module.id,
        label: module.name
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '),
        value: module.name,
      })) || [];

    return (
      <CommonDropdown
        id="Name"
        name="Name"
        label="Name"
        options={[{ key: '', label: 'Select Name', value: '' }, ...options]}
        value={formik.values.name}
        setValue={(value: string) => {
          const selectedModule = options.find(option => option.value === value);
          if (selectedModule) {
            setSelectedModuleId(selectedModule.key);
            setSelectedModuleName(value);
            formik.setFieldValue('name', value);
            formik.setFieldValue('userId', selectedModule.key);
          }
        }}
      />
    );
  };

  const renderCompanyDropdown = () => {
    // Check if companies are available and if items exist
    if (typedCompanies?.items?.length === 0) {
      return <p>No companies available</p>;
    }

    const options =
      typedCompanies?.items?.map(company => ({
        key: company.id,
        label: company.name,
        value: company.id,
      })) || [];

    return (
      <CommonDropdown
        id="company"
        name="companyId"
        label="Company"
        options={[{ key: '', label: 'Select Company', value: '' }, ...options]}
        value={formik.values.companyId}
        setValue={(value: string) => {
          setSelectedCompany(value);
          formik.setFieldValue('companyId', value);
        }}
      />
    );
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: '60%',
          height: '400px',
          // height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          padding: '0 20px',
          background: '#F3F4F8',
          overflow: 'hidden',
        },
      }}
    >
      <UserAdditionHeader>
        Update Email for Module
        <CloseIcon src={closeSvg} onClick={onRequestClose} />
      </UserAdditionHeader>

      <UserAdditionForm onSubmit={formik.handleSubmit}>
        <UserAdditionBody>
          <InputContainer>
            {/* Module Name and Company Name in same row */}
            <UserAdditionFormRow>
              <CommonInputDiv>{renderModuleDropdown()}</CommonInputDiv>
              <CommonInputDiv>{renderCompanyDropdown()}</CommonInputDiv>
            </UserAdditionFormRow>

            {/* Email Input in a new row */}
            <UserAdditionFormRow>
              <CommonInputDiv>
                <CommonInput
                  id="email"
                  name="email"
                  label="Email"
                  placeholder="Type here..."
                  onChange={e => formik.setFieldValue('email', e.target.value)}
                />
              </CommonInputDiv>
            </UserAdditionFormRow>

            {/* Save Button */}
            <UserAdditionFormRowLast>
              <RegularButton
                type="submit"
                style={{ marginTop: '35px', marginLeft: '87%' }}
                disabled={formik.isSubmitting || !formik.values.companyId || selectedModuleName === ''}
              >
                Save
              </RegularButton>
            </UserAdditionFormRowLast>
          </InputContainer>
        </UserAdditionBody>
      </UserAdditionForm>
    </ReactModal>
  );
};

export default UpdateSubModuleEmail;
